import React, { useState, useEffect, useRef } from 'react';
import { masterSupabase as supabase } from '../../../lib/supabase-master';
import { 
  Building2, 
  MapPin, 
  Calendar, 
  Ruler, 
  Zap, 
  Euro, 
  TrendingUp, 
  Award, 
  Users, 
  Flame,
  BarChart3,
  Clock,
  Shield,
  Home,
  BookOpen,
  ExternalLink,
  CheckCircle,
  AlertCircle,
  Target,
  Thermometer,
  Factory,
  Settings,
  Lightbulb,
  Brain,
  Heart
} from 'lucide-react';

// Using masterSupabase client from central instance

const FoerdermittelTab = () => {
  const [activeTab, setActiveTab] = useState('analyse');
  const [immobilienData, setImmobilienData] = useState({
    adresse: '',
    gebaeudetyp: 'Wohngebäude',
    baujahr: '',
    nutzflaeche: '',
    anzahlEinheiten: '',
    energieklasse: 'D (ausreichend)',
    aktuelleHeizung: 'Gas-Brennwertkessel',
    energieverbrauch: '',
    energieausweis: 'Ja',
    sanierungsart: 'Heizungstausch',
    zielStandard: 'KfW 55',
    investmentBudget: '',
    umsetzungszeitraum: '6-12 Monate',
    denkmalschutz: 'Nein',
    nutzungsart: 'Selbstnutzung',
    vorhandenefoerderungen: 'Keine'
  });
  
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [portfolioData, setPortfolioData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [supabaseImmobilien, setSupabaseImmobilien] = useState([]);
  const dropdownRef = useRef(null);

  // Supabase-Daten laden
  useEffect(() => {
    const loadSupabaseProperties = async () => {
      try {
        const { data, error } = await supabase
          .from('properties')
          .select(`
            id,
            property_name,
            address,
            street,
            city,
            postal_code,
            property_type,
            construction_year,
            total_square_meters,
            rentable_area,
            units,
            property_type
          `)
          .eq('property_status', 'active')
          .limit(20);

        if (error) {
          console.error('Supabase Error:', error);
          // Fallback zu Demo-Daten bei Fehler
          setSupabaseImmobilien(getDemoImmobilien());
        } else {
          // Supabase-Daten in unser Format konvertieren
          const convertedData = data.map(property => ({
            id: property.id,
            adresse: property.address || `${property.street}, ${property.postal_code} ${property.city}`,
            gebaeudetyp: mapPropertyType(property.property_type, property.property_type),
            baujahr: property.construction_year?.toString() || '',
            nutzflaeche: property.total_square_meters?.toString() || property.rentable_area?.toString() || '',
            anzahlEinheiten: property.units?.toString() || '1',
            energieklasse: 'D (ausreichend)', // Default, da nicht in DB
            aktuelleHeizung: 'Gas-Brennwertkessel', // Default, da nicht in DB
            energieverbrauch: '', // Default, da nicht in DB
            energieausweis: 'Ja', // Default, da nicht in DB
            details: `${property.property_type || 'Immobilie'} • ${property.construction_year || 'Baujahr unbekannt'} • ${property.total_square_meters || property.rentable_area || '?'}m²${property.units && property.units > 1 ? ` • ${property.units} WE` : ''}`
          }));
          
          setSupabaseImmobilien(convertedData);
        }
      } catch (error) {
        console.error('Fehler beim Laden der Supabase-Daten:', error);
        // Fallback zu Demo-Daten
        setSupabaseImmobilien(getDemoImmobilien());
      }
    };

    loadSupabaseProperties();
  }, []);

  // Property Type Mapping
  const mapPropertyType = (propertyType, buildingType) => {
    if (buildingType === 'commercial') return 'Gewerbegebäude';
    if (buildingType === 'mixed') return 'Mischnutzung';
    
    switch (propertyType?.toLowerCase()) {
      case 'mehrfamilienhaus': return 'Mehrfamilienhaus';
      case 'einfamilienhaus': return 'Einfamilienhaus';
      case 'gewerbe': return 'Gewerbegebäude';
      default: return 'Wohngebäude';
    }
  };

  // Fallback Demo-Daten
  const getDemoImmobilien = () => [
    {
      id: 'demo-1',
      adresse: 'P7,1, 68161 Mannheim',
      gebaeudetyp: 'Gewerbegebäude',
      baujahr: '1980',
      nutzflaeche: '120',
      anzahlEinheiten: '1',
      energieklasse: 'D (ausreichend)',
      aktuelleHeizung: 'Gas-Brennwertkessel',
      energieverbrauch: '180',
      energieausweis: 'Ja',
      details: 'gewerbe • 1980 • 120m²'
    },
    {
      id: 'demo-2',
      adresse: 'Scheffelstraße 8, 68165 Mannheim',
      gebaeudetyp: 'Mehrfamilienhaus',
      baujahr: '1975',
      nutzflaeche: '280',
      anzahlEinheiten: '4',
      energieklasse: 'E (mangelhaft)',
      aktuelleHeizung: 'Öl-Heizkessel',
      energieverbrauch: '320',
      energieausweis: 'Ja',
      details: 'wohnen • 1975 • 280m² • 4 WE'
    }
  ];

  // Erweiterte Förderanalyse-Engine
  const analyzeForecasting = () => {
    setIsLoading(true);
    
    setTimeout(() => {
      const baujahr = parseInt(immobilienData.baujahr) || 1980;
      const nutzflaeche = parseInt(immobilienData.nutzflaeche) || 120;
      const budget = parseInt(immobilienData.investmentBudget) || 100000;
      
      // Intelligente Scoring-Faktoren
      const ageScore = Math.max(0, Math.min(100, (2025 - baujahr) * 2));
      const sizeScore = Math.min(100, nutzflaeche / 5);
      const energyScore = getEnergyClassScore(immobilienData.energieklasse);
      const heatingScore = getHeatingScore(immobilienData.aktuelleHeizung);
      const typeScore = getBuildingTypeScore(immobilienData.gebaeudetyp);
      const measureScore = getMeasureScore(immobilienData.sanierungsart);
      const budgetScore = Math.min(100, budget / 2000);
      const locationScore = getLocationScore(immobilienData.adresse);
      
      const totalScore = Math.round(
        (ageScore * 0.15 + sizeScore * 0.1 + energyScore * 0.2 + 
         heatingScore * 0.15 + typeScore * 0.1 + measureScore * 0.15 + 
         budgetScore * 0.1 + locationScore * 0.05)
      );

      // Förderempfehlungen basierend auf Analyse
      const programs = generateFoerderempfehlungen(immobilienData, totalScore);
      
      setAnalysisResult({
        intelligenceScore: Math.min(100, totalScore),
        potentialSavings: Math.round(budget * 0.3),
        roiYears: Math.max(3, Math.round(15 - (totalScore / 10))),
        programs: programs,
        recommendations: generateRecommendations(immobilienData, totalScore)
      });
      
      setIsLoading(false);
    }, 2000);
  };

  // Hilfsfunktionen für Scoring
  const getEnergyClassScore = (energyClass) => {
    const scores = {
      'A+ (sehr gut)': 20, 'A (sehr gut)': 25, 'B (gut)': 35,
      'C (befriedigend)': 50, 'D (ausreichend)': 70, 'E (mangelhaft)': 85,
      'F (ungenügend)': 95, 'G (sehr schlecht)': 100, 'H (sehr schlecht)': 100
    };
    return scores[energyClass] || 70;
  };

  const getHeatingScore = (heating) => {
    const scores = {
      'Öl-Heizkessel': 90, 'Gas-Heizkessel': 80, 'Gas-Brennwertkessel': 60,
      'Wärmepumpe': 20, 'Fernwärme': 40, 'Pelletheizung': 30,
      'Solarthermie': 15, 'Elektroheizung': 95
    };
    return scores[heating] || 70;
  };

  const getBuildingTypeScore = (type) => {
    const scores = {
      'Einfamilienhaus': 60, 'Mehrfamilienhaus': 70, 'Gewerbegebäude': 80,
      'Mischnutzung': 75, 'Nichtwohngebäude': 85
    };
    return scores[type] || 70;
  };

  const getMeasureScore = (measure) => {
    const scores = {
      'Heizungstausch': 80, 'Dämmung': 70, 'Fenster': 50,
      'Komplettsanierung': 90, 'Solarthermie': 60, 'Wärmepumpe': 85
    };
    return scores[measure] || 70;
  };

  const getLocationScore = (address) => {
    // Sanierungsgebiet-Erkennung mit detaillierter Bewertung
    const sanierungsgebiete = {
      // Neckarstadt-West (noch 9 Jahre)
      'draisstrasse': { score: 95, name: 'Neckarstadt-West', bonus: 0.05, endjahr: 2033 },
      'bürgermeister-fuchs': { score: 95, name: 'Neckarstadt-West', bonus: 0.05, endjahr: 2033 },
      'waldhofstrasse': { score: 95, name: 'Neckarstadt-West', bonus: 0.05, endjahr: 2033 },
      
      // Ilvesheim (nur noch 2 Jahre!)
      'scheffelstrasse': { score: 100, name: 'Ilvesheim Ortskern', bonus: 0.03, endjahr: 2026 },
      'ilvesheim': { score: 90, name: 'Ilvesheim Ortskern', bonus: 0.03, endjahr: 2026 }
    };
    
    const addressLower = address.toLowerCase();
    
    // Prüfe auf Sanierungsgebiete
    for (const [key, gebiet] of Object.entries(sanierungsgebiete)) {
      if (addressLower.includes(key)) {
        return gebiet.score;
      }
    }
    
    // Standard-Bewertung nach Stadt
    if (address.includes('Mannheim')) return 75;
    if (address.includes('Ilvesheim')) return 80;
    return 70;
  };

  // Sanierungsgebiet-Details abrufen
  const getSanierungsgebietDetails = (address) => {
    const sanierungsgebiete = {
      'draisstrasse': { name: 'Neckarstadt-West', bonus: 0.05, endjahr: 2033, dringlichkeit: 'normal' },
      'bürgermeister-fuchs': { name: 'Neckarstadt-West', bonus: 0.05, endjahr: 2033, dringlichkeit: 'normal' },
      'waldhofstrasse': { name: 'Neckarstadt-West', bonus: 0.05, endjahr: 2033, dringlichkeit: 'normal' },
      'scheffelstrasse': { name: 'Ilvesheim Ortskern', bonus: 0.03, endjahr: 2026, dringlichkeit: 'hoch' },
      'ilvesheim': { name: 'Ilvesheim Ortskern', bonus: 0.03, endjahr: 2026, dringlichkeit: 'hoch' }
    };
    
    const addressLower = address.toLowerCase();
    
    for (const [key, gebiet] of Object.entries(sanierungsgebiete)) {
      if (addressLower.includes(key)) {
        return {
          ...gebiet,
          verbleibendeJahre: gebiet.endjahr - new Date().getFullYear()
        };
      }
    }
    
    return null;
  };

  // Förderempfehlungen generieren
  const generateFoerderempfehlungen = (data, score) => {
    const programs = [];
    const sanierungsgebiet = getSanierungsgebietDetails(data.adresse);
    
    // KfW-Programme
    if (data.sanierungsart === 'Komplettsanierung' || data.sanierungsart === 'Dämmung') {
      programs.push({
        name: 'KfW 261 - Wohngebäude Kredit',
        type: 'Kredit + Tilgungszuschuss',
        amount: Math.round(parseInt(data.investmentBudget || 100000) * 0.4),
        rate: '0,01% - 2,24%',
        description: 'Energieeffizient Sanieren - Kredit mit Tilgungszuschuss',
        match: Math.min(95, score + 10)
      });
    }

    if (data.sanierungsart === 'Heizungstausch' || data.aktuelleHeizung === 'Öl-Heizkessel') {
      programs.push({
        name: 'BAFA BEG EM - Heizungstausch',
        type: 'Zuschuss',
        amount: Math.round(parseInt(data.investmentBudget || 100000) * 0.35),
        rate: 'bis zu 70%',
        description: 'Bundesförderung für effiziente Gebäude - Einzelmaßnahmen',
        match: Math.min(98, score + 15)
      });
    }

    if (data.gebaeudetyp === 'Gewerbegebäude') {
      programs.push({
        name: 'KfW 295 - Bundesförderung Nichtwohngebäude',
        type: 'Kredit',
        amount: Math.round(parseInt(data.investmentBudget || 100000) * 0.5),
        rate: '0,01% - 1,83%',
        description: 'Energieeffizient Sanieren - Nichtwohngebäude',
        match: Math.min(92, score + 5)
      });
    }

    // Sanierungsgebiet-Boni hinzufügen
    if (sanierungsgebiet) {
      const baseAmount = parseInt(data.investmentBudget || 100000);
      const bonusAmount = Math.round(baseAmount * sanierungsgebiet.bonus);
      
      programs.push({
        name: `Sanierungsgebiet-Bonus ${sanierungsgebiet.name}`,
        type: 'Zusätzlicher Zuschuss',
        amount: bonusAmount,
        rate: `+${(sanierungsgebiet.bonus * 100).toFixed(0)}%`,
        description: `Zusätzliche Förderung für Sanierungsgebiete (noch ${sanierungsgebiet.verbleibendeJahre} Jahre gültig)`,
        match: 100,
        urgent: sanierungsgebiet.dringlichkeit === 'hoch'
      });

      // §7h EStG Sonder-AfA für Sanierungsgebiete
      programs.push({
        name: '§7h EStG Sonder-AfA',
        type: 'Steuerliche Abschreibung',
        amount: Math.round(baseAmount * 0.09), // 9% jährlich
        rate: '9% p.a.',
        description: `Erhöhte Abschreibung in Sanierungsgebieten (bis ${sanierungsgebiet.endjahr})`,
        match: 100,
        urgent: sanierungsgebiet.dringlichkeit === 'hoch'
      });
    }

    // Mannheim städtische Förderung
    if (data.adresse.includes('Mannheim')) {
      programs.push({
        name: 'Stadt Mannheim Gebäudesanierung',
        type: 'Städtischer Zuschuss',
        amount: Math.min(12000, Math.round(parseInt(data.investmentBudget || 100000) * 0.1)),
        rate: 'bis zu 10%',
        description: 'Zusätzlich zur Bundesförderung, max. 12.000€',
        match: Math.min(90, score + 5)
      });
    }

    return programs.sort((a, b) => b.match - a.match);
  };

  // Empfehlungen generieren
  const generateRecommendations = (data, score) => {
    const recommendations = [];
    const sanierungsgebiet = getSanierungsgebietDetails(data.adresse);
    
    // Sanierungsgebiet-spezifische Empfehlungen
    if (sanierungsgebiet) {
      if (sanierungsgebiet.dringlichkeit === 'hoch') {
        recommendations.push(`⚠️ DRINGEND: Sanierungsgebiet ${sanierungsgebiet.name} läuft nur noch ${sanierungsgebiet.verbleibendeJahre} Jahre!`);
        recommendations.push('Sofortige Antragstellung empfohlen - Steuervorteile gehen sonst verloren');
      } else {
        recommendations.push(`✅ Sanierungsgebiet-Vorteile: Noch ${sanierungsgebiet.verbleibendeJahre} Jahre Zeit für optimale Förderung`);
      }
      
      recommendations.push(`💰 Zusätzliche ${(sanierungsgebiet.bonus * 100).toFixed(0)}% Förderung + 9% AfA durch Sanierungsgebiet-Status`);
    }
    
    // Standard-Empfehlungen basierend auf Score
    if (score > 80) {
      recommendations.push('Sehr hohes Förderpotenzial - Antrag zeitnah stellen');
      recommendations.push('Energieeffizienz-Experte hinzuziehen für optimale Förderung');
    } else if (score > 60) {
      recommendations.push('Gutes Förderpotenzial - Maßnahmen kombinieren für höhere Förderung');
      recommendations.push('Prüfung weiterer Sanierungsmaßnahmen empfohlen');
    } else {
      recommendations.push('Moderates Förderpotenzial - Fokus auf effizienteste Maßnahmen');
      recommendations.push('Beratung durch Energieberater empfohlen');
    }
    
    // Mannheim-spezifische Empfehlungen
    if (data.adresse.includes('Mannheim')) {
      recommendations.push('🏛️ Zusätzliche städtische Förderung Mannheim verfügbar (bis 12.000€)');
    }
    
    return recommendations;
  };

  // Portfolio-Analyse
  const analyzePortfolio = () => {
    setIsLoading(true);
    
    setTimeout(() => {
      const portfolioImmobilien = supabaseImmobilien.map(immobilie => {
        const score = Math.round(Math.random() * 40 + 60);
        const potential = Math.round(Math.random() * 200000 + 50000);
        const roi = Math.round(Math.random() * 15 + 10);
        
        return {
          ...immobilie,
          foerderpotenzial: potential,
          roi: roi,
          amortisation: Math.round(potential / (potential * 0.05))
        };
      });

      const totalPotential = portfolioImmobilien.reduce((sum, immo) => sum + immo.foerderpotenzial, 0);
      const avgRoi = Math.round(portfolioImmobilien.reduce((sum, immo) => sum + immo.roi, 0) / portfolioImmobilien.length);
      const avgAmortisation = Math.round(portfolioImmobilien.reduce((sum, immo) => sum + immo.amortisation, 0) / portfolioImmobilien.length);

      setPortfolioData({
        immobilienCount: portfolioImmobilien.length,
        totalPotential: totalPotential,
        avgRoi: avgRoi,
        avgAmortisation: avgAmortisation,
        topOpportunities: portfolioImmobilien
          .sort((a, b) => b.foerderpotenzial - a.foerderpotenzial)
          .slice(0, 3)
      });
      
      setIsLoading(false);
    }, 1500);
  };

  // Adress-Suche
  const handleAddressSearch = (value) => {
    setImmobilienData(prev => ({ ...prev, adresse: value }));
    
    if (value.length > 1) {
      const filtered = supabaseImmobilien.filter(immo =>
        immo.adresse.toLowerCase().includes(value.toLowerCase())
      );
      setSearchResults(filtered);
      setShowDropdown(filtered.length > 0);
    } else {
      setSearchResults([]);
      setShowDropdown(false);
    }
  };

  // Immobilie auswählen mit vollständigem Autofill
  const selectImmobilie = (immobilie) => {
    setImmobilienData(prev => ({
      ...prev,
      adresse: immobilie.adresse,
      gebaeudetyp: immobilie.gebaeudetyp,
      baujahr: immobilie.baujahr,
      nutzflaeche: immobilie.nutzflaeche,
      anzahlEinheiten: immobilie.anzahlEinheiten,
      energieklasse: immobilie.energieklasse,
      aktuelleHeizung: immobilie.aktuelleHeizung,
      energieverbrauch: immobilie.energieverbrauch,
      energieausweis: immobilie.energieausweis
    }));
    setShowDropdown(false);
    setSearchResults([]);
  };

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="w-full space-y-6 text-gray-900 dark:text-gray-100 text-gray-700">
      {/* Header */}
      <div className="flex items-center space-x-3 mb-6">
        <div className="p-2 bg-purple-100 dark:bg-purple-900 bg-purple-200 rounded-lg">
          <Award className="h-6 w-6 text-purple-600 dark:text-purple-400 text-purple-700" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white text-gray-800">
            Ultimative Fördermittel-Engine
          </h2>
          <div className="flex items-center space-x-4 mt-1">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900 bg-purple-200 text-purple-800 dark:text-purple-200 text-purple-700">
              <Zap className="w-3 h-3 mr-1" />
              KI-powered
            </span>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 bg-green-200 text-green-800 dark:text-green-200 text-green-700">
              <CheckCircle className="w-3 h-3 mr-1" />
              Transparent
            </span>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 text-gray-500 mt-1">
            KI-optimierte Förder-Analyse mit Transparenz & Lerneffekt
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-gray-100 dark:bg-gray-800 bg-gray-200 p-1 rounded-lg">
        <button
          onClick={() => setActiveTab('analyse')}
          className={`flex-1 flex items-center justify-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'analyse'
              ? 'bg-white dark:bg-gray-700 bg-gray-50 text-purple-600 dark:text-purple-400 text-purple-700 shadow-sm'
              : 'text-gray-600 dark:text-gray-400 text-gray-500 hover:text-gray-900 dark:hover:text-gray-200 hover:text-gray-800'
          }`}
        >
          <BarChart3 className="w-4 h-4 mr-2" />
          Intelligente Analyse
        </button>
        <button
          onClick={() => setActiveTab('portfolio')}
          className={`flex-1 flex items-center justify-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'portfolio'
              ? 'bg-white dark:bg-gray-700 bg-gray-50 text-purple-600 dark:text-purple-400 text-purple-700 shadow-sm'
              : 'text-gray-600 dark:text-gray-400 text-gray-500 hover:text-gray-900 dark:hover:text-gray-200 hover:text-gray-800'
          }`}
        >
          <Building2 className="w-4 h-4 mr-2" />
          Portfolio-Übersicht
        </button>
        <button
          onClick={() => setActiveTab('lernen')}
          className={`flex-1 flex items-center justify-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'lernen'
              ? 'bg-white dark:bg-gray-700 bg-gray-50 text-purple-600 dark:text-purple-400 text-purple-700 shadow-sm'
              : 'text-gray-600 dark:text-gray-400 text-gray-500 hover:text-gray-900 dark:hover:text-gray-200 hover:text-gray-800'
          }`}
        >
          <BookOpen className="w-4 h-4 mr-2" />
          Selbst lernen
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'analyse' && (
        <div className="space-y-6">
          {/* Intelligente Förder-Analyse */}
          <div className="bg-white dark:bg-gray-800 bg-gray-50 rounded-xl p-6 border border-gray-200 dark:border-gray-700 border-gray-300">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-purple-100 dark:bg-purple-900 bg-purple-200 rounded-lg">
                <Target className="h-5 w-5 text-purple-600 dark:text-purple-400 text-purple-700" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-gray-800">
                  Intelligente Förder-Analyse
                </h3>
                <div className="flex items-center space-x-2 mt-1">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900 bg-purple-200 text-purple-800 dark:text-purple-200 text-purple-700">
                    <Zap className="w-3 h-3 mr-1" />
                    KI-optimiert
                  </span>
                </div>
              </div>
            </div>

            {/* Immobilien-Daten */}
            <div className="bg-gray-50 dark:bg-gray-900 bg-gray-100 rounded-lg p-6 mb-6">
              <div className="flex items-center space-x-2 mb-4">
                <Building2 className="h-5 w-5 text-gray-600 dark:text-gray-400 text-gray-500" />
                <h4 className="text-md font-medium text-gray-900 dark:text-white text-gray-800">
                  Immobilien-Daten
                </h4>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Immobilie auswählen */}
                <div className="relative" ref={dropdownRef}>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300 text-gray-600 mb-2">
                    <MapPin className="h-4 w-4" />
                    <span>Immobilie auswählen</span>
                  </label>
                  <input
                    type="text"
                    value={immobilienData.adresse}
                    onChange={(e) => handleAddressSearch(e.target.value)}
                    placeholder="Adresse eingeben oder auswählen..."
                    className="w-full h-12 px-4 border border-gray-300 dark:border-gray-600 border-gray-400 rounded-lg bg-white dark:bg-gray-800 bg-gray-50 text-gray-900 dark:text-white text-gray-800 placeholder-gray-500 dark:placeholder-gray-400 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  
                  {showDropdown && searchResults.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 bg-gray-50 border border-gray-300 dark:border-gray-600 border-gray-400 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {searchResults.map((immobilie) => (
                        <div
                          key={immobilie.id}
                          onClick={() => selectImmobilie(immobilie)}
                          className="px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 hover:bg-gray-200 cursor-pointer border-b border-gray-200 dark:border-gray-700 border-gray-300 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900 dark:text-white text-gray-800">
                            {immobilie.adresse}
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400 text-gray-500">
                            {immobilie.details}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Gebäudetyp */}
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300 text-gray-600 mb-2">
                    <Building2 className="h-4 w-4" />
                    <span>Gebäudetyp</span>
                  </label>
                  <select
                    value={immobilienData.gebaeudetyp}
                    onChange={(e) => setImmobilienData(prev => ({ ...prev, gebaeudetyp: e.target.value }))}
                    className="w-full h-12 px-4 border border-gray-300 dark:border-gray-600 border-gray-400 rounded-lg bg-white dark:bg-gray-800 bg-gray-50 text-gray-900 dark:text-white text-gray-800 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="Einfamilienhaus">Einfamilienhaus</option>
                    <option value="Mehrfamilienhaus">Mehrfamilienhaus</option>
                    <option value="Gewerbegebäude">Gewerbegebäude</option>
                    <option value="Mischnutzung">Mischnutzung</option>
                    <option value="Nichtwohngebäude">Nichtwohngebäude</option>
                  </select>
                </div>

                {/* Baujahr */}
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300 text-gray-600 mb-2">
                    <Calendar className="h-4 w-4" />
                    <span>Baujahr</span>
                  </label>
                  <input
                    type="number"
                    value={immobilienData.baujahr}
                    onChange={(e) => setImmobilienData(prev => ({ ...prev, baujahr: e.target.value }))}
                    placeholder="z.B. 1980"
                    min="1900"
                    max="2025"
                    className="w-full h-12 px-4 border border-gray-300 dark:border-gray-600 border-gray-400 rounded-lg bg-white dark:bg-gray-800 bg-gray-50 text-gray-900 dark:text-white text-gray-800 placeholder-gray-500 dark:placeholder-gray-400 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                {/* Nutzfläche */}
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300 text-gray-600 mb-2">
                    <Ruler className="h-4 w-4" />
                    <span>Nutzfläche (m²)</span>
                  </label>
                  <input
                    type="number"
                    value={immobilienData.nutzflaeche}
                    onChange={(e) => setImmobilienData(prev => ({ ...prev, nutzflaeche: e.target.value }))}
                    placeholder="z.B. 120"
                    min="1"
                    className="w-full h-12 px-4 border border-gray-300 dark:border-gray-600 border-gray-400 rounded-lg bg-white dark:bg-gray-800 bg-gray-50 text-gray-900 dark:text-white text-gray-800 placeholder-gray-500 dark:placeholder-gray-400 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                {/* Anzahl Einheiten */}
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300 text-gray-600 mb-2">
                    <Users className="h-4 w-4" />
                    <span>Anzahl Einheiten</span>
                  </label>
                  <input
                    type="number"
                    value={immobilienData.anzahlEinheiten}
                    onChange={(e) => setImmobilienData(prev => ({ ...prev, anzahlEinheiten: e.target.value }))}
                    placeholder="z.B. 1"
                    min="1"
                    className="w-full h-12 px-4 border border-gray-300 dark:border-gray-600 border-gray-400 rounded-lg bg-white dark:bg-gray-800 bg-gray-50 text-gray-900 dark:text-white text-gray-800 placeholder-gray-500 dark:placeholder-gray-400 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                {/* Energieklasse */}
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300 text-gray-600 mb-2">
                    <Thermometer className="h-4 w-4" />
                    <span>Energieklasse</span>
                  </label>
                  <select
                    value={immobilienData.energieklasse}
                    onChange={(e) => setImmobilienData(prev => ({ ...prev, energieklasse: e.target.value }))}
                    className="w-full h-12 px-4 border border-gray-300 dark:border-gray-600 border-gray-400 rounded-lg bg-white dark:bg-gray-800 bg-gray-50 text-gray-900 dark:text-white text-gray-800 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="A+ (sehr gut)">A+ (sehr gut)</option>
                    <option value="A (sehr gut)">A (sehr gut)</option>
                    <option value="B (gut)">B (gut)</option>
                    <option value="C (befriedigend)">C (befriedigend)</option>
                    <option value="D (ausreichend)">D (ausreichend)</option>
                    <option value="E (mangelhaft)">E (mangelhaft)</option>
                    <option value="F (ungenügend)">F (ungenügend)</option>
                    <option value="G (sehr schlecht)">G (sehr schlecht)</option>
                    <option value="H (sehr schlecht)">H (sehr schlecht)</option>
                  </select>
                </div>

                {/* Aktuelle Heizung */}
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300 text-gray-600 mb-2">
                    <Flame className="h-4 w-4" />
                    <span>Aktuelle Heizung</span>
                  </label>
                  <select
                    value={immobilienData.aktuelleHeizung}
                    onChange={(e) => setImmobilienData(prev => ({ ...prev, aktuelleHeizung: e.target.value }))}
                    className="w-full h-12 px-4 border border-gray-300 dark:border-gray-600 border-gray-400 rounded-lg bg-white dark:bg-gray-800 bg-gray-50 text-gray-900 dark:text-white text-gray-800 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="Öl-Heizkessel">Öl-Heizkessel</option>
                    <option value="Gas-Heizkessel">Gas-Heizkessel</option>
                    <option value="Gas-Brennwertkessel">Gas-Brennwertkessel</option>
                    <option value="Wärmepumpe">Wärmepumpe</option>
                    <option value="Fernwärme">Fernwärme</option>
                    <option value="Pelletheizung">Pelletheizung</option>
                    <option value="Solarthermie">Solarthermie</option>
                    <option value="Elektroheizung">Elektroheizung</option>
                  </select>
                </div>

                {/* Energieverbrauch */}
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300 text-gray-600 mb-2">
                    <Zap className="h-4 w-4" />
                    <span>Energieverbrauch (kWh/Jahr)</span>
                  </label>
                  <input
                    type="number"
                    value={immobilienData.energieverbrauch}
                    onChange={(e) => setImmobilienData(prev => ({ ...prev, energieverbrauch: e.target.value }))}
                    placeholder="z.B. 180"
                    min="0"
                    className="w-full h-12 px-4 border border-gray-300 dark:border-gray-600 border-gray-400 rounded-lg bg-white dark:bg-gray-800 bg-gray-50 text-gray-900 dark:text-white text-gray-800 placeholder-gray-500 dark:placeholder-gray-400 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                {/* Sanierungsart */}
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300 text-gray-600 mb-2">
                    <Settings className="h-4 w-4" />
                    <span>Geplante Sanierungsart</span>
                  </label>
                  <select
                    value={immobilienData.sanierungsart}
                    onChange={(e) => setImmobilienData(prev => ({ ...prev, sanierungsart: e.target.value }))}
                    className="w-full h-12 px-4 border border-gray-300 dark:border-gray-600 border-gray-400 rounded-lg bg-white dark:bg-gray-800 bg-gray-50 text-gray-900 dark:text-white text-gray-800 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="Heizungstausch">Heizungstausch</option>
                    <option value="Dämmung">Dämmung</option>
                    <option value="Fenster">Fenster</option>
                    <option value="Komplettsanierung">Komplettsanierung</option>
                    <option value="Solarthermie">Solarthermie</option>
                    <option value="Wärmepumpe">Wärmepumpe</option>
                  </select>
                </div>

                {/* Investment-Budget */}
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300 text-gray-600 mb-2">
                    <Euro className="h-4 w-4" />
                    <span>Investment-Budget (€)</span>
                  </label>
                  <input
                    type="number"
                    value={immobilienData.investmentBudget}
                    onChange={(e) => setImmobilienData(prev => ({ ...prev, investmentBudget: e.target.value }))}
                    placeholder="z.B. 100000"
                    min="0"
                    className="w-full h-12 px-4 border border-gray-300 dark:border-gray-600 border-gray-400 rounded-lg bg-white dark:bg-gray-800 bg-gray-50 text-gray-900 dark:text-white text-gray-800 placeholder-gray-500 dark:placeholder-gray-400 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-gray-400 mt-1">
                    Geplante Investitionssumme für Sanierungsmaßnahmen
                  </p>
                </div>

                {/* Umsetzungszeitraum */}
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300 text-gray-600 mb-2">
                    <Clock className="h-4 w-4" />
                    <span>Umsetzungszeitraum</span>
                  </label>
                  <select
                    value={immobilienData.umsetzungszeitraum}
                    onChange={(e) => setImmobilienData(prev => ({ ...prev, umsetzungszeitraum: e.target.value }))}
                    className="w-full h-12 px-4 border border-gray-300 dark:border-gray-600 border-gray-400 rounded-lg bg-white dark:bg-gray-800 bg-gray-50 text-gray-900 dark:text-white text-gray-800 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="Sofort">Sofort</option>
                    <option value="3-6 Monate">3-6 Monate</option>
                    <option value="6-12 Monate">6-12 Monate</option>
                    <option value="1-2 Jahre">1-2 Jahre</option>
                    <option value="Langfristig">Langfristig</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Analyse Button */}
            <div className="flex justify-center">
              <button
                onClick={analyzeForecasting}
                disabled={isLoading}
                className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                    Analysiere...
                  </>
                ) : (
                  <>
                    <Target className="h-5 w-5 mr-3" />
                    Intelligente Analyse
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Analysis Results */}
          {analysisResult && (
            <div className="bg-white dark:bg-gray-800 bg-gray-50 rounded-xl p-6 border border-gray-200 dark:border-gray-700 border-gray-300">
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 bg-green-100 dark:bg-green-900 bg-green-200 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-green-600 dark:text-green-400 text-green-700" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-gray-800">
                  Analyse-Ergebnisse
                </h3>
              </div>

              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900 dark:to-purple-800 from-purple-100 to-purple-200 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-purple-600 dark:text-purple-400 text-purple-700">Intelligence Score</p>
                      <p className="text-2xl font-bold text-purple-900 dark:text-purple-100 text-purple-800">
                        {analysisResult.intelligenceScore}/100
                      </p>
                    </div>
                    <Award className="h-8 w-8 text-purple-600 dark:text-purple-400 text-purple-700" />
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900 dark:to-green-800 from-green-100 to-green-200 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-600 dark:text-green-400 text-green-700">Förder-Potenzial</p>
                      <p className="text-2xl font-bold text-green-900 dark:text-green-100 text-green-800">
                        {analysisResult.potentialSavings.toLocaleString()} €
                      </p>
                    </div>
                    <Euro className="h-8 w-8 text-green-600 dark:text-green-400 text-green-700" />
                  </div>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 from-blue-100 to-blue-200 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-600 dark:text-blue-400 text-blue-700">ROI-Amortisation</p>
                      <p className="text-2xl font-bold text-blue-900 dark:text-blue-100 text-blue-800">
                        {analysisResult.roiYears} Jahre
                      </p>
                    </div>
                    <TrendingUp className="h-8 w-8 text-blue-600 dark:text-blue-400 text-blue-700" />
                  </div>
                </div>
              </div>

              {/* Förderempfehlungen */}
              <div className="space-y-4">
                <h4 className="text-md font-medium text-gray-900 dark:text-white text-gray-800 mb-4">
                  Empfohlene Förderprogramme
                </h4>
                {analysisResult.programs.map((program, index) => (
                  <div key={index} className="bg-gray-50 dark:bg-gray-900 bg-gray-100 rounded-lg p-4 border border-gray-200 dark:border-gray-700 border-gray-300">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h5 className="font-medium text-gray-900 dark:text-white text-gray-800">
                            {program.name}
                          </h5>
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 bg-green-200 text-green-800 dark:text-green-200 text-green-700">
                            {program.match}% Match
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 text-gray-500 mb-2">
                          {program.description}
                        </p>
                        <div className="flex items-center space-x-4 text-sm">
                          <span className="text-gray-600 dark:text-gray-400 text-gray-500">
                            <strong>Typ:</strong> {program.type}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400 text-gray-500">
                            <strong>Zinssatz:</strong> {program.rate}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-green-600 dark:text-green-400 text-green-700">
                          {program.amount.toLocaleString()} €
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 text-gray-400">
                          max. Förderung
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Empfehlungen */}
              <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900 bg-blue-100 rounded-lg">
                <h4 className="text-md font-medium text-blue-900 dark:text-blue-100 text-blue-800 mb-3">
                  Handlungsempfehlungen
                </h4>
                <ul className="space-y-2">
                  {analysisResult.recommendations.map((rec, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <CheckCircle className="h-4 w-4 text-blue-600 dark:text-blue-400 text-blue-700 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-blue-800 dark:text-blue-200 text-blue-700">{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Portfolio Tab */}
      {activeTab === 'portfolio' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 bg-gray-50 rounded-xl p-6 border border-gray-200 dark:border-gray-700 border-gray-300">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900 bg-blue-200 rounded-lg">
                  <Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400 text-blue-700" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-gray-800">
                    Portfolio-Übersicht
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 text-gray-500">
                    {supabaseImmobilien.length} Immobilien verfügbar für Analyse
                  </p>
                </div>
              </div>
              <button
                onClick={analyzePortfolio}
                disabled={isLoading}
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Analysiere...
                  </>
                ) : (
                  <>
                    <BarChart3 className="h-4 w-4 mr-2" />
                    Portfolio analysieren
                  </>
                )}
              </button>
            </div>

            {portfolioData ? (
              <div className="space-y-6">
                {/* Portfolio KPIs */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 from-blue-100 to-blue-200 p-4 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-blue-600 dark:text-blue-400 text-blue-700">Immobilien</p>
                        <p className="text-2xl font-bold text-blue-900 dark:text-blue-100 text-blue-800">
                          {portfolioData.immobilienCount}
                        </p>
                      </div>
                      <Building2 className="h-6 w-6 text-blue-600 dark:text-blue-400 text-blue-700" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900 dark:to-green-800 from-green-100 to-green-200 p-4 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-green-600 dark:text-green-400 text-green-700">Förder-Potenzial</p>
                        <p className="text-2xl font-bold text-green-900 dark:text-green-100 text-green-800">
                          {portfolioData.totalPotential.toLocaleString()} €
                        </p>
                      </div>
                      <Euro className="h-6 w-6 text-green-600 dark:text-green-400 text-green-700" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900 dark:to-purple-800 from-purple-100 to-purple-200 p-4 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-purple-600 dark:text-purple-400 text-purple-700">Portfolio-ROI</p>
                        <p className="text-2xl font-bold text-purple-900 dark:text-purple-100 text-purple-800">
                          {portfolioData.avgRoi}%
                        </p>
                      </div>
                      <TrendingUp className="h-6 w-6 text-purple-600 dark:text-purple-400 text-purple-700" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900 dark:to-orange-800 from-orange-100 to-orange-200 p-4 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-orange-600 dark:text-orange-400 text-orange-700">Jahre Amortisation</p>
                        <p className="text-2xl font-bold text-orange-900 dark:text-orange-100 text-orange-800">
                          {portfolioData.avgAmortisation}
                        </p>
                      </div>
                      <Clock className="h-6 w-6 text-orange-600 dark:text-orange-400 text-orange-700" />
                    </div>
                  </div>
                </div>

                {/* Top-3 Förder-Chancen */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 dark:text-white text-gray-800 mb-4">
                    Top-3 Förder-Chancen
                  </h4>
                  <div className="space-y-3">
                    {portfolioData.topOpportunities.map((immobilie, index) => (
                      <div key={immobilie.id} className="bg-gray-50 dark:bg-gray-900 bg-gray-100 rounded-lg p-4 border border-gray-200 dark:border-gray-700 border-gray-300">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white ${
                              index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : 'bg-orange-500'
                            }`}>
                              #{index + 1}
                            </div>
                            <div>
                              <h5 className="font-medium text-gray-900 dark:text-white text-gray-800">
                                {immobilie.adresse}
                              </h5>
                              <p className="text-sm text-gray-600 dark:text-gray-400 text-gray-500">
                                {immobilie.details}
                              </p>
                            </div>
                          </div>
                          <div className="text-right space-y-1">
                            <div className="flex items-center space-x-4">
                              <div>
                                <p className="text-sm text-gray-600 dark:text-gray-400 text-gray-500">Förder-Potenzial:</p>
                                <p className="font-bold text-green-600 dark:text-green-400 text-green-700">
                                  {immobilie.foerderpotenzial.toLocaleString()} €
                                </p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-600 dark:text-gray-400 text-gray-500">ROI:</p>
                                <p className="font-bold text-purple-600 dark:text-purple-400 text-purple-700">
                                  {immobilie.roi}%
                                </p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-600 dark:text-gray-400 text-gray-500">Amortisation:</p>
                                <p className="font-bold text-blue-600 dark:text-blue-400 text-blue-700">
                                  {immobilie.amortisation} Jahre
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <BarChart3 className="h-8 w-8 text-gray-400 dark:text-gray-500 text-gray-500" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white text-gray-800 mb-2">
                  Portfolio-Analyse starten
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-gray-500 mb-6">
                  Analysieren Sie alle Ihre Immobilien gleichzeitig und finden Sie die besten Förder-Chancen.
                </p>
                <button
                  onClick={analyzePortfolio}
                  className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
                >
                  <BarChart3 className="h-5 w-5 mr-2" />
                  Zur Analyse
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Selbst lernen Tab */}
      {activeTab === 'lernen' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 bg-gray-50 rounded-xl p-6 border border-gray-200 dark:border-gray-700 border-gray-300">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-indigo-100 dark:bg-indigo-900 bg-indigo-200 rounded-lg">
                <BookOpen className="h-5 w-5 text-indigo-600 dark:text-indigo-400 text-indigo-700" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-gray-800">
                  Selbst lernen
                </h3>
                <div className="flex items-center space-x-2 mt-1">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 dark:bg-indigo-900 bg-indigo-200 text-indigo-800 dark:text-indigo-200 text-indigo-700">
                    <BookOpen className="w-3 h-3 mr-1" />
                    Wissensaufbau
                  </span>
                </div>
              </div>
            </div>

            {/* Poetisches Intro */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900 dark:to-purple-900 from-indigo-100 to-purple-100 rounded-lg p-6 mb-8 border border-indigo-200 dark:border-indigo-700 border-indigo-300">
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full">
                  <Brain className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-indigo-900 dark:text-indigo-100 text-indigo-800 mb-3 flex items-center">
                    <Lightbulb className="h-5 w-5 mr-2" />
                    Die Kunst des Verstehens in einer KI-Welt
                  </h4>
                  <div className="prose prose-indigo dark:prose-invert prose-sm max-w-none">
                    <p className="text-indigo-800 dark:text-indigo-200 text-indigo-700 leading-relaxed mb-3">
                      In einer Zeit, in der künstliche Intelligenz komplexe Berechnungen in Sekunden vollzieht, mag es paradox erscheinen, 
                      sich selbst in die Tiefen der Förderlandschaft zu begeben. Doch wahre Expertise entsteht nicht durch das blinde 
                      Vertrauen in Algorithmen, sondern durch das Verstehen der Zusammenhänge dahinter.
                    </p>
                    <p className="text-indigo-800 dark:text-indigo-200 text-indigo-700 leading-relaxed mb-3">
                      Jede Förderung erzählt eine Geschichte – von politischen Zielen, wirtschaftlichen Anreizen und gesellschaftlichen 
                      Visionen. Wer diese Geschichten versteht, kann nicht nur die Empfehlungen der KI besser einordnen, sondern auch 
                      Chancen erkennen, die noch kein Algorithmus erfasst hat.
                    </p>
                    <p className="text-indigo-800 dark:text-indigo-200 text-indigo-700 leading-relaxed flex items-center">
                      <Heart className="h-4 w-4 mr-2 text-red-500" />
                      <em>Lernen Sie nicht trotz der KI, sondern mit ihr – als Partner auf dem Weg zur Meisterschaft.</em>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* KfW-Förderprogramme */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 from-blue-100 to-blue-200 rounded-lg p-6 flex flex-col">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-blue-600 rounded-lg">
                    <Building2 className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-900 dark:text-blue-100 text-blue-800">
                      KfW-Förderprogramme verstehen
                    </h4>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-200 dark:bg-blue-800 bg-blue-300 text-blue-800 dark:text-blue-200 text-blue-700">
                      Offiziell
                    </span>
                  </div>
                </div>
                <div className="flex-grow">
                  <p className="text-sm text-blue-800 dark:text-blue-200 text-blue-700 mb-4 leading-relaxed">
                    Die Kreditanstalt für Wiederaufbau bietet über 100 verschiedene Förderprogramme. Von der energetischen Sanierung 
                    bis zum Neubau – verstehen Sie die Systematik hinter den Programmnummern und Förderbedingungen. Lernen Sie, 
                    welche Programme kombinierbar sind und wie Sie die maximale Förderung erreichen.
                  </p>
                </div>
                <a
                  href="https://www.kfw.de/inlandsfoerderung/Privatpersonen/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors mt-auto"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Mehr erfahren
                </a>
              </div>

              {/* BAFA-Förderung */}
              <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900 dark:to-green-800 from-green-100 to-green-200 rounded-lg p-6 flex flex-col">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-green-600 rounded-lg">
                    <Zap className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-green-900 dark:text-green-100 text-green-800">
                      BAFA-Förderung beantragen
                    </h4>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-200 dark:bg-green-800 bg-green-300 text-green-800 dark:text-green-200 text-green-700">
                      Offiziell
                    </span>
                  </div>
                </div>
                <div className="flex-grow">
                  <p className="text-sm text-green-800 dark:text-green-200 text-green-700 mb-4 leading-relaxed">
                    Das Bundesamt für Wirtschaft und Ausfuhrkontrolle verwaltet die Bundesförderung für effiziente Gebäude (BEG). 
                    Erfahren Sie, wie Sie Heizungsförderungen von bis zu 70% erhalten, welche Technologien gefördert werden und 
                    wie der Antragsprozess funktioniert. Timing ist hier entscheidend.
                  </p>
                </div>
                <a
                  href="https://www.bafa.de/DE/Energie/Effiziente_Gebaeude/effiziente_gebaeude_node.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors mt-auto"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Mehr erfahren
                </a>
              </div>

              {/* Förderdatenbank */}
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900 dark:to-purple-800 from-purple-100 to-purple-200 rounded-lg p-6 flex flex-col">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-purple-600 rounded-lg">
                    <BarChart3 className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-purple-900 dark:text-purple-100 text-purple-800">
                      Förderdatenbank des Bundes
                    </h4>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-200 dark:bg-purple-800 bg-purple-300 text-purple-800 dark:text-purple-200 text-purple-700">
                      Recherche
                    </span>
                  </div>
                </div>
                <div className="flex-grow">
                  <p className="text-sm text-purple-800 dark:text-purple-200 text-purple-700 mb-4 leading-relaxed">
                    Über 6.000 Förderprogramme von Bund, Ländern, EU und Kommunen in einer Datenbank. Lernen Sie die erweiterte 
                    Suchfunktion zu nutzen, verstehen Sie die Förderlandschaft systematisch und entdecken Sie Nischenprogramme, 
                    die perfekt zu Ihrem Projekt passen könnten.
                  </p>
                </div>
                <a
                  href="https://www.foerderdatenbank.de/FDB/DE/Foerderprogramme/foerderprogramme.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors mt-auto"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Mehr erfahren
                </a>
              </div>

              {/* Energieberater */}
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900 dark:to-orange-800 from-orange-100 to-orange-200 rounded-lg p-6 flex flex-col">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-orange-600 rounded-lg">
                    <Users className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-orange-900 dark:text-orange-100 text-orange-800">
                      Energieberater finden
                    </h4>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-200 dark:bg-orange-800 bg-orange-300 text-orange-800 dark:text-orange-200 text-orange-700">
                      Service
                    </span>
                  </div>
                </div>
                <div className="flex-grow">
                  <p className="text-sm text-orange-800 dark:text-orange-200 text-orange-700 mb-4 leading-relaxed">
                    Qualifizierte Energieeffizienz-Experten sind für viele Förderprogramme Pflicht. Verstehen Sie die verschiedenen 
                    Qualifikationen, lernen Sie die richtige Auswahl zu treffen und erfahren Sie, wie eine gute Beratung Ihre 
                    Fördersumme erheblich steigern kann. Die Beratung selbst wird oft gefördert.
                  </p>
                </div>
                <a
                  href="https://www.energie-effizienz-experten.de/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg transition-colors mt-auto"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Mehr erfahren
                </a>
              </div>

              {/* Steuerliche Förderung */}
              <div className="bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900 dark:to-red-800 from-red-100 to-red-200 rounded-lg p-6 flex flex-col">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-red-600 rounded-lg">
                    <Euro className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-red-900 dark:text-red-100 text-red-800">
                      Steuerliche Förderung
                    </h4>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-200 dark:bg-red-800 bg-red-300 text-red-800 dark:text-red-200 text-red-700">
                      Steuer
                    </span>
                  </div>
                </div>
                <div className="flex-grow">
                  <p className="text-sm text-red-800 dark:text-red-200 text-red-700 mb-4 leading-relaxed">
                    Alternative zur direkten Förderung: 20% der Sanierungskosten über drei Jahre steuerlich absetzen. Verstehen Sie 
                    die Vor- und Nachteile gegenüber Direktförderungen, lernen Sie die Kombinationsmöglichkeiten kennen und 
                    erfahren Sie, wann sich welcher Weg mehr lohnt.
                  </p>
                </div>
                <a
                  href="https://www.bundesfinanzministerium.de/Content/DE/Standardartikel/Themen/Schlaglichter/Nachhaltigkeitsstrategie/steuerliche-foerderung-energetischer-gebaeudesanierungen.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors mt-auto"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Mehr erfahren
                </a>
              </div>

              {/* Regionale Förderungen */}
              <div className="bg-gradient-to-br from-teal-50 to-teal-100 dark:from-teal-900 dark:to-teal-800 from-teal-100 to-teal-200 rounded-lg p-6 flex flex-col">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-teal-600 rounded-lg">
                    <MapPin className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-teal-900 dark:text-teal-100 text-teal-800">
                      Regionale Förderungen BW
                    </h4>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-teal-200 dark:bg-teal-800 bg-teal-300 text-teal-800 dark:text-teal-200 text-teal-700">
                      Regional
                    </span>
                  </div>
                </div>
                <div className="flex-grow">
                  <p className="text-sm text-teal-800 dark:text-teal-200 text-teal-700 mb-4 leading-relaxed">
                    Baden-Württemberg bietet zusätzliche Landesprogramme und kommunale Förderungen. Verstehen Sie die Förderlandschaft 
                    vor Ihrer Haustür, lernen Sie lokale Besonderheiten kennen und erfahren Sie, wie Sie Bundes-, Landes- und 
                    kommunale Förderungen intelligent kombinieren können.
                  </p>
                </div>
                <a
                  href="https://um.baden-wuerttemberg.de/de/presse-service/foerderprogramme/energie/energetische-foerderprogramme-fuer-gebaeude"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-lg transition-colors mt-auto"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Mehr erfahren
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FoerdermittelTab;
